import React from 'react';
import {ChevronDownIcon} from '@heroicons/react/24/outline';
import {getResourceIcon} from '../../../../../helpers/iconHelper';
import {freshness, unitSymbol, resolutionLabel} from '../../../../../helpers/marketProfile';
import ResourceDetails, {freshnessLabel} from '../../../../../components/ResourceDetails';
import moment from 'moment';

// Program documentation page for a resource, from the slug the API publishes.
const programUrl = (slug) => (slug ? `/docs/programs/${slug}/` : null);

// Freshness of the measurement feed as a header pill: amber once the series
// falls behind by more than a day's worth of its own intervals.
function FreshnessPill({state, latest}) {
  if (state === 'unknown') return null;
  const tone = state === 'fresh'
    ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
    : 'bg-amber-100 text-amber-800 dark:bg-amber-900/25 dark:text-amber-300';
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${tone}`}>
      {freshnessLabel(state, latest)}
    </span>
  );
}

export default function ResourceCard({resource: r, isExpanded, onToggleExpand, onEdit, onDelete, onViewManagers, getManagerEntry, getManagerEntries, managersLoading, isAddEnabled, isAdmin, copyId, copyJSON, formatRoleLabel, isOwned}) {
  const managers = getManagerEntries ? getManagerEntries(r) : [];
  const mm = r.measurements_metadata || null;
  const profile = r.profile || {};
  // Managers, audit timestamps and the raw payload are the market owner's and
  // the admin's tools. A forecaster gets the owner as one contact line.
  const showOperatorDetail = !!(isAdmin || isAddEnabled);
  const {state: freshnessState} = freshness(
    mm?.end_datetime, profile.resolution_minutes
  );
  const budget = parseFloat(r.budget);
  const spec = [unitSymbol(profile.unit), resolutionLabel(profile.resolution_minutes)]
    .filter(Boolean)
    .join(' · ');

  const toggle = () => {
    onToggleExpand && onToggleExpand();
    if (!isExpanded && isAddEnabled && onEdit) onEdit(r, {keepExpanded: true});
  };

  return (
    // Phone: each resource is its own bordered card, so the footer bar below
    // reads as part of it. Desktop keeps the borderless rows inside the panel.
    <div className={`overflow-hidden rounded-lg border border-gray-200/70 transition-all lg:border-0 dark:border-gray-600/70 ${r.is_active !== false ? '' : 'bg-gray-100/50 dark:bg-gray-870/50 opacity-60 hover:opacity-75'}`}>
      <div
        className="flex cursor-pointer items-start justify-between gap-3 px-3 py-3 hover:bg-gray-50 lg:gap-4 lg:p-4 dark:hover:bg-gray-700/30"
        onClick={toggle}
      >
        <div className="flex min-w-0 flex-1 items-start gap-3">
          <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg lg:h-10 lg:w-10 lg:border lg:border-gray-300 lg:dark:border-gray-600">
            {getResourceIcon(r)}
          </div>
          <div className="min-w-0 flex-1">
            {/* Phone: name on its own line, pills under it. Desktop: one wrapping row. */}
            <div className="flex flex-col gap-1.5 lg:flex-row lg:flex-wrap lg:items-center lg:gap-2">
              {/* A resource id is unbreakable at phone width; wrap it rather than
                  truncate, since the tail is what tells two markets apart. */}
              <span
                className="[overflow-wrap:anywhere] font-mono text-[12.5px] font-semibold leading-snug text-gray-900 lg:text-sm lg:[overflow-wrap:normal] dark:text-gray-100"
                title={r.name}
              >
                {r.name}
              </span>
              <span className="flex flex-wrap items-center gap-1.5 lg:contents">
                {r.is_active !== false && (
                  <span className="inline-flex items-center rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800 dark:bg-green-900/30 dark:text-green-300">
                    Active
                  </span>
                )}
                <FreshnessPill state={freshnessState} latest={mm?.end_datetime}/>
                {isOwned && !managersLoading && (
                  <span className="inline-flex items-center rounded-md bg-brand-100 px-2 py-0.5 text-xs font-medium text-brand-700 dark:bg-brand-900/30 dark:text-brand-300">
                    Owned
                  </span>
                )}
              </span>
            </div>

            <div className="mt-0.5 hidden truncate text-xs text-gray-500 lg:block dark:text-gray-400" title={r.email}>
              {r.email || '—'}
              {spec && <span className="ml-2 font-mono">{spec}</span>}
            </div>

            {profile.target_summary && (
              <p className="mt-1.5 max-w-[60ch] text-[13px] leading-snug text-gray-700 lg:text-[13.5px] dark:text-gray-300">
                {profile.target_summary}
              </p>
            )}

            {/* Phone: the reward has nowhere to sit in the right-hand column. */}
            <div className="mt-2 flex flex-wrap items-baseline gap-x-2 gap-y-1 lg:hidden">
              {Number.isFinite(budget) && budget > 0 && (
                <>
                  <span className="font-mono text-sm font-semibold tabular-nums text-gray-900 dark:text-gray-100">
                    €{budget.toLocaleString(undefined, {maximumFractionDigits: 0})}
                  </span>
                  <span className="text-[10px] uppercase tracking-[0.1em] text-gray-500 dark:text-gray-400">
                    monthly reward
                  </span>
                </>
              )}
              {spec && (
                <span className="ml-auto font-mono text-[11px] text-gray-500 dark:text-gray-400">{spec}</span>
              )}
            </div>
          </div>
        </div>

        <div className="flex flex-shrink-0 items-center gap-2 lg:ml-4 lg:gap-4">
          {Number.isFinite(budget) && budget > 0 && (
            <div className="hidden text-right leading-tight lg:block">
              <div className="font-mono text-[17px] font-semibold tabular-nums tracking-tight text-gray-900 dark:text-gray-100">
                €{budget.toLocaleString(undefined, {maximumFractionDigits: 0})}
              </div>
              <div className="mt-0.5 text-[9px] uppercase tracking-[0.11em] text-gray-500 dark:text-gray-400">
                monthly reward
              </div>
            </div>
          )}
          {isAddEnabled && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onEdit(r); }}
              className="rounded-lg p-2 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-brand-500 dark:hover:bg-gray-600"
              title="Edit resource"
            >
              <svg className="h-4 w-4 text-gray-600 dark:text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/></svg>
            </button>
          )}
          {isAdmin && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onDelete(r); }}
              className="rounded-lg p-2 hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-red-500 dark:hover:bg-red-900/20"
              title="Delete resource"
            >
              <svg className="h-4 w-4 text-red-600 dark:text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/></svg>
            </button>
          )}
          <button
            type="button"
            aria-expanded={isExpanded}
            aria-controls={`resource-details-${r.id}`}
            onClick={(e) => { e.stopPropagation(); toggle(); }}
            className="hidden items-center gap-1 whitespace-nowrap rounded-lg px-1.5 py-1 text-xs font-medium text-brand-600 hover:underline underline-offset-4 focus:outline-none focus:ring-2 focus:ring-brand-500 lg:inline-flex dark:text-brand-400"
          >
            {isExpanded ? 'Hide' : 'Details'}
            <ChevronDownIcon className={`h-4 w-4 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}/>
          </button>
        </div>
      </div>


      {isExpanded && (
        <div id={`resource-details-${r.id}`} role="region" aria-label={`${r.name} details`}>
          <ResourceDetails
            resource={r}
            programUrl={programUrl(profile.program_slug)}
            onCopyRequest={(request) => navigator.clipboard.writeText(request)}
            onCopyId={copyId}
          />

          {showOperatorDetail && (
            <div className="hidden border-t border-dashed border-gray-200 px-5 py-4 lg:block dark:border-gray-700">
              <div className="mb-3 text-[10px] font-bold uppercase tracking-[0.12em] text-gray-500 dark:text-gray-400">
                Owner &amp; admin
              </div>

              {!managersLoading && managers && managers.length > 0 && (
                <div className="mb-4 space-y-2 text-xs">
                  {managers.map((m, i) => (
                    <div key={i} className="flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <div className="truncate text-gray-700 dark:text-gray-300">{m.manager_name || m.user_name || m.email || '—'}</div>
                        {m.user_email && <div className="truncate text-gray-500">{m.user_email}</div>}
                      </div>
                      <div className="flex flex-shrink-0 items-center gap-2">
                        <span className="inline-flex items-center rounded-md bg-brand-100 px-2 py-1 text-xs font-medium text-brand-700 dark:bg-brand-900/30 dark:text-brand-300">
                          {formatRoleLabel(m)}
                        </span>
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); navigator.clipboard.writeText(m.user_email || ''); }}
                          className="rounded-md border border-gray-200/70 bg-surface px-2 py-1 text-xs text-gray-700 hover:bg-gray-50 dark:border-gray-600/70 dark:bg-gray-870 dark:text-gray-200"
                        >
                          Copy email
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <div className="grid grid-cols-1 gap-x-6 gap-y-2 text-xs sm:grid-cols-3">
                <div>
                  <div className="text-gray-500 dark:text-gray-400">Resource ID</div>
                  <div className="break-words font-mono text-gray-700 dark:text-gray-300">{r.id}</div>
                </div>
                <div>
                  <div className="text-gray-500 dark:text-gray-400">Registered</div>
                  <div className="font-mono text-gray-700 dark:text-gray-300">
                    {r.registered_at ? moment(r.registered_at).format('lll') : 'N/A'}
                  </div>
                </div>
                <div>
                  <div className="text-gray-500 dark:text-gray-400">Updated</div>
                  <div className="font-mono text-gray-700 dark:text-gray-300">
                    {r.updated_at ? moment(r.updated_at).fromNow() : 'N/A'}
                  </div>
                </div>
              </div>

              <div className="mt-3 flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); copyJSON(r); }}
                  className="rounded-lg border border-gray-300 bg-surface px-3 py-1.5 text-xs text-gray-700 transition-colors hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-870 dark:text-gray-200 dark:hover:bg-gray-600"
                >
                  Copy JSON
                </button>
                {onViewManagers && (
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); onViewManagers(r); }}
                    className="rounded-lg border border-gray-300 bg-surface px-3 py-1.5 text-xs text-gray-700 transition-colors hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-870 dark:text-gray-200 dark:hover:bg-gray-600"
                  >
                    Manage managers
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Phone: a full-width bar is the tap target, not a text link in the corner. */}
      <button
        type="button"
        aria-expanded={isExpanded}
        aria-controls={`resource-details-${r.id}`}
        onClick={toggle}
        className="flex min-h-[44px] w-full items-center justify-center gap-1.5 border-t border-gray-100 bg-gray-50/70 text-[12.5px] font-medium text-brand-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 lg:hidden dark:border-gray-700 dark:bg-gray-800/40 dark:text-brand-400"
      >
        {isExpanded ? 'Hide' : 'Details'}
        <ChevronDownIcon className={`h-3.5 w-3.5 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}/>
      </button>
    </div>
  );
}
